import { useTranslation } from "react-i18next";
import { Dispatch, SetStateAction, useEffect, useMemo } from "react";

import { TextField, Autocomplete, CircularProgress } from "@mui/material";
import { Person2Outlined } from "@mui/icons-material";

import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Modal } from "@/components";

import { ERROR_TYPE, translateError } from "@/utils";

import { useGetAssigneesQuery } from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks.api";

import { ToDoPriority } from "../enum/ToDoPriority.enum";
import {
	SubTaskInfo,
	TaskInfo,
	UpdateSubTaskInfo,
	UpdateSubTaskInfoSchema,
	UpdateTaskInfo,
	UpdateTaskInfoSchema,
} from "../schema/tasks-and-subtasks";
import { ToDoStatus } from "../enum/ToDoStatus.enum";
import { useToDoSocket } from "../../ToDo/ToDoList/providers/SocketContext";

interface CreateNewTaskModalProps {
	isOpen: boolean;
	setIsOpen?: Dispatch<SetStateAction<boolean>>;
	data: SubTaskInfo | TaskInfo | null;
}

export default function AssigneeModal({ setIsOpen, isOpen, data }: CreateNewTaskModalProps) {
	const { t } = useTranslation();
	const ts = (key: string, options = {}) => t(`createTask.${key}`, options);

	const { updateTask, updateSubTask } = useToDoSocket();

	const defaultValues: UpdateTaskInfo | UpdateSubTaskInfo = useMemo(
		() => ({
			id: data?.id ?? 0,
			title: data?.title ?? "",
			description: data?.description ?? "",
			assigneeId: data?.assigneeId ?? null,
			priority: data?.priority ?? ToDoPriority.MEDIUM,
			tagId: data?.tagId ?? undefined,
			taskId: data && "taskId" in data ? data.taskId : undefined,
			status: ToDoStatus.QA,
			complexityEstimate: data?.complexityEstimate ?? 2,
		}),
		[data]
	);

	useEffect(() => {
		reset(defaultValues);
	}, [defaultValues]);

	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<UpdateTaskInfo | UpdateSubTaskInfo>({
		defaultValues,
		resolver: zodResolver(
			data && "taskId" in data ? UpdateSubTaskInfoSchema : UpdateTaskInfoSchema
		),
	});

	const { data: users, isLoading: isUsersLoading } = useGetAssigneesQuery();

	const unassignedOption = {
		firstName: t("createTask.unassigned"),
		lastName: "",
		assigneeId: null,
	};

	const assigneeOptions = [
		unassignedOption,
		...(users?.map((user) => ({
			firstName: user.firstName,
			lastName: user.lastName,
			assigneeId: +user.id,
		})) ?? []),
	];

	const onSubmit = (values: UpdateTaskInfo | UpdateSubTaskInfo) => {
		if ("taskId" in values && values.taskId) {
			updateSubTask(values.taskId, values.id, {
				...values,
				tagId: data?.tagId ?? undefined,
				assigneeId: values.assigneeId,
				status: ToDoStatus.QA,
			});
		} else {
			updateTask(values.id, {
				...values,
				tagId: data?.tagId ?? undefined,
				assigneeId: values.assigneeId,
				status: ToDoStatus.QA,
			});
		}

		setIsOpen?.(false);
	};

	const isInitialLoading = isUsersLoading;

	return (
		<Modal
			// aboveHeader={<Icon icon={faList} />}
			dialogContentClassName="!px-4 max-h-[80vh] overflow-auto mr-1 custom-scrollbar"
			handleClose={() => {
				reset(defaultValues);
				setIsOpen?.(false);
			}}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isUsersLoading}
			isOpened={isOpen}
			saveButtonContainerClassName="!w-full"
			size="sm"
			submitButtonText={ts("save")}
			subtitle={data?.isTask ? t("to-do.qa-assignee-task") : t("to-do.qa-assignee-subtask")}
			title={t("to-do.qa-assignee")}
		>
			{isInitialLoading ? (
				<div className="w-full flex justify-center py-6">
					<CircularProgress size={24} />
				</div>
			) : (
				<Controller
					control={control}
					name="assigneeId"
					render={({ field }) => (
						<Autocomplete
							disableClearable
							className="w-full"
							getOptionLabel={(o) => `${o.firstName ?? ""} ${o.lastName ?? ""}`.trim()}
							isOptionEqualToValue={(a, b) => {
								if (a?.assigneeId === null && b?.assigneeId === null) {
									return true;
								}

								return a?.assigneeId === b?.assigneeId;
							}}
							options={assigneeOptions}
							renderInput={(params) => (
								<TextField
									{...params}
									InputProps={{
										...params.InputProps,
										startAdornment: (
											<>
												<Person2Outlined
													sx={{ color: "action.active", mr: 0.5, my: 0.5, fontSize: 18 }}
												/>
												{params.InputProps.startAdornment}
											</>
										),
									}}
									error={!!errors?.assigneeId}
									helperText={
										!!errors?.assigneeId?.message
											? translateError[errors?.assigneeId?.message as unknown as ERROR_TYPE]
											: errors?.assigneeId?.message
									}
									label={ts("assignee")}
								/>
							)}
							size="small"
							value={
								field.value === null
									? unassignedOption
									: assigneeOptions.find((o) => o?.assigneeId === field.value) ?? unassignedOption
							}
							onChange={(_, v) => field.onChange(v?.assigneeId ?? null)}
						/>
					)}
				/>
			)}
		</Modal>
	);
}
